import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { supabase } from "@/integrations/supabase/client";
import {
  getQueuedSavedResumes,
  removeQueuedSavedResume,
  updateQueuedSavedResumeStatus,
} from "@/lib/offlineResumeQueue";

export function OfflineSyncManager() {
  const { user } = useAuth();
  const isOnline = useOnlineStatus();
  const syncingRef = useRef(false);

  useEffect(() => {
    if (!user || !isOnline || syncingRef.current) return;

    const syncQueuedResumes = async () => {
      syncingRef.current = true;

      try {
        const queued = await getQueuedSavedResumes(user.id);
        if (queued.length === 0) return;

        let synced = 0;
        for (const item of queued) {
          await updateQueuedSavedResumeStatus(item.id, "syncing");

          const { error } = await supabase.from("saved_resumes").insert({
            user_id: item.userId,
            title: item.title,
            content: item.content,
          });

          if (error) {
            await updateQueuedSavedResumeStatus(item.id, "failed");
            console.error("Offline resume sync failed", error);
            continue;
          }

          await removeQueuedSavedResume(item.id);
          synced += 1;
        }

        if (synced > 0) {
          toast.success(`${synced} offline resume${synced === 1 ? "" : "s"} synced to your account.`);
        }
      } finally {
        syncingRef.current = false;
      }
    };

    syncQueuedResumes();
  }, [isOnline, user]);

  return null;
}
